import { isApiRequestError } from "./apiRequestError";
import { humanizeApiUserCopy } from "./humanizeApiUserCopy";

export type UserFacingApiError = {
  message: string;
  action?: string;
  slug?: string;
};

function toUserFacing(text: string | undefined, fallback: string): string {
  const raw = text?.trim() || fallback;
  return humanizeApiUserCopy(raw);
}

/** Map API errors to Sonner copy: message primary, action secondary. */
export function formatApiErrorForToast(
  err: unknown,
  fallbackTitle: string
): { title: string; description?: string } {
  if (isApiRequestError(err)) {
    const title = toUserFacing(err.message, fallbackTitle);
    const workerError =
      typeof err.details?.worker_error === "string"
        ? err.details.worker_error.trim()
        : "";
    const description = err.action?.trim()
      ? humanizeApiUserCopy(err.action.trim())
      : workerError
        ? humanizeApiUserCopy(workerError)
        : undefined;
    return { title, description };
  }

  if (err instanceof Error && err.message.trim()) {
    return {
      title: humanizeApiUserCopy(fallbackTitle),
      description: humanizeApiUserCopy(err.message.trim()),
    };
  }

  return { title: humanizeApiUserCopy(fallbackTitle) };
}

/** User-facing copy from message + optional action (e.g. broker test-connection body). */
export function formatUserFacingApiCopy(options: {
  message?: string;
  action?: string;
  fallbackTitle: string;
}): { title: string; description?: string } {
  const title = toUserFacing(options.message, options.fallbackTitle);
  const description = options.action?.trim()
    ? humanizeApiUserCopy(options.action.trim())
    : undefined;
  return { title, description };
}

/** Extract display fields from any thrown API error. */
export function getApiErrorDisplay(
  err: unknown,
  fallback = "Something went wrong."
): UserFacingApiError {
  if (isApiRequestError(err)) {
    return {
      message: toUserFacing(err.message, fallback),
      action: err.action?.trim() ? humanizeApiUserCopy(err.action.trim()) : undefined,
      slug: err.slug,
    };
  }
  if (err instanceof Error && err.message.trim()) {
    return { message: humanizeApiUserCopy(err.message.trim()) };
  }
  if (typeof err === "string" && err.trim()) {
    return { message: humanizeApiUserCopy(err.trim()) };
  }
  return { message: humanizeApiUserCopy(fallback) };
}

/** Primary + secondary lines for inline alerts (message + action). */
export function formatApiErrorInlineLines(
  err: unknown,
  fallback: string
): { primary: string; secondary?: string } {
  const display = getApiErrorDisplay(err, fallback);
  return {
    primary: display.message,
    secondary: display.action,
  };
}

export type ReportUserErrorPayload = {
  error?: string;
  message?: string;
  action?: string;
};

/** Normalize report `user_error` (object or legacy string) for display. */
export function parseReportUserError(
  userError: unknown,
  legacyErrorMessage?: string | null
): UserFacingApiError {
  if (userError && typeof userError === "object" && !Array.isArray(userError)) {
    const row = userError as ReportUserErrorPayload;
    const message = typeof row.message === "string" ? row.message.trim() : "";
    const action = typeof row.action === "string" ? row.action.trim() : undefined;
    const slug = typeof row.error === "string" ? row.error : undefined;
    if (message) {
      return {
        message: humanizeApiUserCopy(message),
        action: action ? humanizeApiUserCopy(action) : undefined,
        slug,
      };
    }
  }

  if (typeof userError === "string" && userError.trim()) {
    return { message: humanizeApiUserCopy(userError.trim()) };
  }

  if (typeof legacyErrorMessage === "string" && legacyErrorMessage.trim()) {
    return { message: humanizeApiUserCopy(legacyErrorMessage.trim()) };
  }

  return {
    message: "Something went wrong while building this report.",
  };
}
